 /* Import TinyMCE */
 import tinymce from 'tinymce';
import { getMermaidApi, initMermaid, sanitizeSource } from './mermaid-renderer.js';

/*/ Required TinyMCE Modules */
import 'tinymce/models/dom/model';

//  /* Default icons are required for TinyMCE 5.3 or above */
import 'tinymce/icons/default';

//  /* A theme is also required */
import 'tinymce/themes/silver';

/* Import plugins */
import "tinymce/plugins/autolink"
import "tinymce/plugins/link"
import "tinymce/plugins/code"
//import "tinymce/plugins/charmap"
import "tinymce/plugins/media"
import "tinymce/plugins/image"
import "tinymce/plugins/accordion"
// import "tinymce/plugins/imagetools"
import "tinymce/plugins/table"
import "tinymce/plugins/advlist"
//import "tinymce/plugins/quickbars"
import "tinymce/plugins/searchreplace"
import "tinymce/plugins/lists"
// import "tinymce/plugins/paste"
import "tinymce/plugins/fullscreen"
import "tinymce/plugins/codesample"
import "tinymce/plugins/autoresize"
import "tinymce/plugins/autosave"
import "tinymce/plugins/directionality"
import "tinymce/plugins/emoticons"
import "tinymce/plugins/emoticons/js/emojis.js"
import '../lib/powerpaste.min.js';
import '../lib/powerpaste-wordimport.js';

// import contentUiCss from 'tinymce/skins/ui/oxide/content.css';
// import contentCss from 'tinymce/skins/content/default/content.css';

const mermaidEditorState = new WeakMap();

function getMermaidEditorState(editor) {
  let state = mermaidEditorState.get(editor);

  if (!state) {
    state = {
      renderTimer: null,
      rendering: false,
      codeSampleEditing: false,
    };
    mermaidEditorState.set(editor, state);
  }

  return state;
}

function updateMermaidEditorState(editor, attributes) {
  const state = getMermaidEditorState(editor);
  Object.assign(state, attributes);
  return state;
}

function clearMermaidRenderTimer(editor) {
  const state = getMermaidEditorState(editor);
  if (!state.renderTimer) return;

  clearTimeout(state.renderTimer);
  state.renderTimer = null;
}

const MERMAID_WRAPPER_CLASS = 'mermaid-diagram-wrapper';
const MERMAID_HIDDEN_ATTR = 'data-mermaid-hidden';
const MERMAID_ID_ATTR = 'data-mermaid-id';
const MERMAID_EDITOR_SELECTOR = 'pre.language-mermaid, pre.mermaid, pre > code.language-mermaid, pre > code.mermaid';

function getMermaidPreBlocks(doc) {
  const blockSet = new Set();

  doc.querySelectorAll(MERMAID_EDITOR_SELECTOR).forEach((node) => {
    if (node.tagName === 'PRE') {
      blockSet.add(node);
    } else if (node.parentElement?.tagName === 'PRE') {
      blockSet.add(node.parentElement);
    }
  });

  return Array.from(blockSet);
}

function getMermaidSourceFromPre(preEl) {
  const codeEl = preEl.querySelector('code.language-mermaid, code.mermaid');
  return sanitizeSource((codeEl || preEl).textContent || '');
}

function hideMermaidSourcePre(preEl) {
  preEl.dataset.mermaidHidden = 'true';
  preEl.style.display = 'none';
}

function showMermaidSourcePre(preEl) {
  preEl.removeAttribute(MERMAID_HIDDEN_ATTR);
  preEl.style.display = '';
}

function ensureMermaidId(preEl) {
  if (!preEl.dataset.mermaidId) {
    preEl.dataset.mermaidId = 'mermaid-src-' + Math.random().toString(36).slice(2, 10);
  }

  return preEl.dataset.mermaidId;
}

function findWrapperByMermaidId(doc, mermaidId) {
  if (!mermaidId) return null;
  return doc.querySelector(`.${MERMAID_WRAPPER_CLASS}[${MERMAID_ID_ATTR}="${mermaidId}"]`);
}

function ensureMermaidWrapper(doc, preEl) {
  const mermaidId = ensureMermaidId(preEl);
  let wrapper = findWrapperByMermaidId(doc, mermaidId);

  if (!wrapper) {
    wrapper = doc.createElement('div');
    wrapper.className = MERMAID_WRAPPER_CLASS;
    wrapper.setAttribute('contenteditable', 'false');
    wrapper.setAttribute(MERMAID_ID_ATTR, mermaidId);
    preEl.insertAdjacentElement('afterend', wrapper);
  } else if (wrapper.getAttribute(MERMAID_ID_ATTR) !== mermaidId) {
    wrapper.setAttribute(MERMAID_ID_ATTR, mermaidId);
  }

  if (preEl.parentNode !== wrapper) {
    wrapper.appendChild(preEl);
  }

  return wrapper;
}

function unwrapMermaidPreviewWrappers(doc) {
  if (!doc) return;

  doc.querySelectorAll(`.${MERMAID_WRAPPER_CLASS}`).forEach((wrapper) => {
    const pre = wrapper.querySelector('pre');
    if (pre) {
      ensureMermaidId(pre);
      wrapper.insertAdjacentElement('beforebegin', pre);
      showMermaidSourcePre(pre);
    }

    wrapper.remove();
  });
}

function restoreMermaidSourceBlocks(root) {
  if (!root) return;

  unwrapMermaidPreviewWrappers(root);

  root.querySelectorAll(`pre[${MERMAID_HIDDEN_ATTR}]`).forEach((preEl) => {
    showMermaidSourcePre(preEl);
  });
}

async function renderMermaidInEditor(editor) {
  const state = getMermaidEditorState(editor);
  if (state.rendering) return;

  const doc = editor.getDoc();
  if (!doc) return;

  const preBlocks = getMermaidPreBlocks(doc);
  if (preBlocks.length === 0) return;

  updateMermaidEditorState(editor, { rendering: true });

  try {
    initMermaid();
    const mermaid = getMermaidApi();
    if (!mermaid || typeof mermaid.render !== 'function') {
      throw new Error('Mermaid API render() is unavailable');
    }

    for (const preEl of preBlocks) {
      if (preEl.closest('.mce-offscreen-selection')) continue;

      const source = getMermaidSourceFromPre(preEl);
      if (!source) continue;

      if (preEl.dataset.mermaidHidden === 'true') {
        const mermaidId = ensureMermaidId(preEl);
        const hasWrapper = !!preEl.closest(`.${MERMAID_WRAPPER_CLASS}`) || !!findWrapperByMermaidId(doc, mermaidId);
        if (!hasWrapper) {
          preEl.remove();
          continue;
        }
      }

      const wrapper = ensureMermaidWrapper(doc, preEl);

      const encodedSource = encodeURIComponent(source);
      if (wrapper.dataset.mermaidSource === encodedSource && wrapper.dataset.mermaidRendered === 'true') {
        hideMermaidSourcePre(preEl);
        continue;
      }

      wrapper.dataset.mermaidSource = encodedSource;
      wrapper.dataset.mermaidRendered = 'false';

      const id = 'mermaid-' + Math.random().toString(36).slice(2, 10);

      try {
        const { svg } = await mermaid.render(id, source);
        wrapper.innerHTML = svg;
        wrapper.dataset.mermaidRendered = 'true';
      } catch (err) {
        const errorMessage = err && err.message ? err.message : 'Invalid syntax';
        const errorWrapper = doc.createElement('div');
        errorWrapper.className = 'mermaid-diagram--error';
        errorWrapper.textContent = 'Diagram error: ' + errorMessage;
        wrapper.replaceChildren(errorWrapper);
        const errorEl = doc.getElementById('d' + id);
        if (errorEl) errorEl.remove();
      }

      wrapper.appendChild(preEl);
      hideMermaidSourcePre(preEl);
    }
  } catch (error) {
    const message = (error && error.message) ? error.message : 'Mermaid render initialization failed';
    for (const preEl of preBlocks) {
      const source = getMermaidSourceFromPre(preEl);
      if (!source) continue;

      const wrapper = ensureMermaidWrapper(doc, preEl);
      wrapper.classList.add('mermaid-diagram--error');
      wrapper.dataset.mermaidSource = encodeURIComponent(source);
      wrapper.textContent = 'Diagram error: ' + message;
      wrapper.appendChild(preEl);
      hideMermaidSourcePre(preEl);
    }
  } finally {
    updateMermaidEditorState(editor, { rendering: false });
  }
}

function scheduleMermaidRender(editor) {
  const state = getMermaidEditorState(editor);
  if (state.codeSampleEditing) return;

  clearMermaidRenderTimer(editor);
  state.renderTimer = setTimeout(() => {
    state.renderTimer = null;
    renderMermaidInEditor(editor);
  }, 150);
}

function restoreMermaidInContent(content) {
  if (!content.includes(MERMAID_WRAPPER_CLASS) && !content.includes(MERMAID_HIDDEN_ATTR)) return content;

  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = content;

  restoreMermaidSourceBlocks(tempDiv);

  return tempDiv.innerHTML;
}

function findMermaidSourcePre(editor, wrapper) {
  const innerPre = wrapper.querySelector('pre');
  if (innerPre) return innerPre;

  const doc = editor.getDoc();
  if (!doc) return null;

  const mermaidId = wrapper.getAttribute(MERMAID_ID_ATTR);
  let pre = mermaidId ? doc.querySelector(`pre[${MERMAID_ID_ATTR}="${mermaidId}"]`) : null;
  if (!pre) {
    pre = wrapper.previousElementSibling;
    if (!pre || !pre.matches('pre')) {
      pre = wrapper.nextElementSibling;
      if (!pre || !pre.matches('pre')) return null;
    }
  }

  return pre;
}

function openMermaidCodeSampleEditor(editor, wrapper) {
  const pre = findMermaidSourcePre(editor, wrapper);
  if (!pre) return false;

  ensureMermaidId(pre);

  wrapper.insertAdjacentElement('beforebegin', pre);
  wrapper.remove();
  showMermaidSourcePre(pre);

  updateMermaidEditorState(editor, { codeSampleEditing: true });
  editor.selection.select(pre);
  editor.nodeChanged();

  // Open TinyMCE's native Code Sample dialog (existing logic + language dropdown).
  const dblClickEvent = new MouseEvent('dblclick', { bubbles: true, cancelable: true });
  pre.dispatchEvent(dblClickEvent);

  return true;
}

function handleMermaidDblClick(editor, e) {
  const wrapper = e.target.closest(`.${MERMAID_WRAPPER_CLASS}`);
  if (!wrapper) return;

  e.preventDefault();
  e.stopPropagation();
  openMermaidCodeSampleEditor(editor, wrapper);
}

function handleMermaidCodeSampleCommand(editor, e) {
  if (e.command !== 'codesample') return;

  const wrapper = editor.selection.getNode()?.closest?.(`.${MERMAID_WRAPPER_CLASS}`);
  if (!wrapper) return;

  e.preventDefault();
  openMermaidCodeSampleEditor(editor, wrapper);
}

export default function initTiny (that, selector, min_height, disable_store_callbacks, custom_css="") {
  const targetEl = document.querySelector(selector);
  const isInTurboModal = targetEl?.closest('.manage__modal__body');
  var dark_mode = document.body.classList.contains('dark-portal') ? 'dark-portal' : '';
  var bgColor = dark_mode ? 'var(--background---neutral---dark--3--default)' : '#fff';
  
  // Safari fix: Override scrollIntoView to prevent TinyMCE iframe from scrolling
  const originalScrollIntoView = Element.prototype.scrollIntoView;
  
  Element.prototype.scrollIntoView = function(...args) {
    // Prevent TinyMCE iframe from scrolling into view on Safari
    if (this.tagName === 'IFRAME' && this.className.includes('tox-edit-area__iframe')) {
      return; // Block the scroll
    }
    return originalScrollIntoView.apply(this, args);
  };
  
  tinymce.init({
    body_class: `rich_text_content ${dark_mode}`,
    menubar: false,
    browser_spellcheck: true,
    contextmenu: false,
    license_key: 'gpl',
    selector: selector,
    inline: false,
    codesample_global_prismjs: true,
    sandbox_iframes: false,
    codesample_languages: [
      { text: 'HTML/XML', value: 'markup' },
      { text: 'JavaScript', value: 'javascript' },
      { text: 'CSS', value: 'css' },
      { text: 'Powershell', value: 'powershell' },
      { text: 'PHP', value: 'php' },
      { text: 'Ruby', value: 'ruby' },
      { text: 'Python', value: 'python' },
      { text: 'Java', value: 'java' },
      { text: 'C', value: 'c' },
      { text: 'C#', value: 'csharp' },
      { text: 'C++', value: 'cpp' },
      { text: 'SQL', value: 'sql' },
      { text: 'Swift', value: 'swift' },
      { text: 'Markdown', value: 'markdown' },
      { text: 'Bash', value: 'bash' },
      { text: 'JSON', value: 'json' },
      { text: 'TOML', value: 'toml' },
      { text: 'Mermaid', value: 'mermaid' }
    ],
    setup: function (editor) {
      editor.on('change', function () {
          editor.save();
          scheduleMermaidRender(editor);
      });

      editor.on('init', function () {
        unwrapMermaidPreviewWrappers(editor.getDoc());
        scheduleMermaidRender(editor);
      });

      editor.on('SetContent', function () {
        unwrapMermaidPreviewWrappers(editor.getDoc());
        scheduleMermaidRender(editor);
      });

      editor.on('input Undo Redo keyup NodeChange', function () {
        scheduleMermaidRender(editor);
      });

      editor.on('GetContent', function (e) {
        if (e.content) {
          e.content = restoreMermaidInContent(e.content);
        }
      });

      editor.on('dblclick', function (e) {
        handleMermaidDblClick(editor, e);
      });

      editor.on('BeforeExecCommand', function (e) {
        handleMermaidCodeSampleCommand(editor, e);
      });

      editor.on('CloseWindow', function () {
        updateMermaidEditorState(editor, { codeSampleEditing: false });
        scheduleMermaidRender(editor);
      });

      editor.on('remove', function () {
        clearMermaidRenderTimer(editor);
        mermaidEditorState.delete(editor);
      });
    },
    auto_focus : min_height ? "mytextarea" : false,
    resize: true,
    skin: false,
    content_css: [
      document.querySelector("#main_application_stylesheet").href
    ],
    content_style: (document.querySelector("#mainApplicationStylesForColors")?.innerHTML || "") + (custom_css || "") + " html, body { background: " + bgColor + "; }",
    relative_urls: false,
    valid_children: "-div[p|h1|h2|h3|h4|h5|h6|blockquote],+div[pre],+div[img]",
    automatic_uploads: true,
    image_title: true,
    image_advtab: true,
    // min_height: min_height ? min_height : false,
    paste_data_images: true,
    paste_as_text: false,
    autosave_ask_before_unload: min_height ? true : false,
    block_formats: 'Header Large=h1;Header Medium=h2;Header Small=h3;Header Tiny=h4;Paragraph=p;Blockquote=blockquote',
    style_formats: [
      {title: "Header Large", format: "h1", styles: {color: '#ff0000'}},
      {title: "Header Medium", format: "h2"},
      {title: "Header Small", format: "h3"},
      {title: "Header Tiny", format: "h4"},
      {title: "Paragraph", format: "p", exact: true, classes: ''},
      {title: "Blockquote", format: "blockquote"},
      {title: "Inline Code", inline: "code"},
      {title: "Callouts", items: [
              {title: "Info", format: 'calloutinfo'},
              {title: "Success", format: 'calloutsuccess'},
              {title: "Warning", format: 'calloutwarning'},
              {title: "Danger", format: 'calloutdanger'}
          ]},
      ],
      //quickbars_insert_toolbar: 'quicktable image media codesample',
      //quickbars_selection_toolbar: 'bold italic underline | blocks | blockquote quicklink',
      formats: {
        removeformat: [
          {
            selector: 'b,strong,em,i,span,u,strike,s,sub,sup,del,ins',
            remove: 'all',
            split: true,
            expand: false,
            block_expand: true,
            deep: true
          },
          {
            selector: 'h1,h2,h3,h4,h5,h6,blockquote,pre,code',
            remove: 'all',
            split: true,
            expand: false,
            block_expand: true,
            deep: true
          },
          {
            selector: 'a,img,table,tr,td,th,tbody,thead,tfoot,div',
            remove: 'all',
            split: true,
            expand: false,
            block_expand: true,
            deep: true
          },
          {
            selector: 'a', // Ensuring links are included
            remove: 'all',
            split: true,
            expand: false,
            block_expand: true,
            deep: true
          },
          {
            selector: '*',
            attributes: ['style', 'class', 'data-mce-style'],
            remove: 'none',
            split: false,
            expand: false,
            deep: true
          }
        ],
        codeeditor: {selector: 'p,h1,h2,h3,h4,h5,h6,td,th,div'},
        alignleft: {selector: 'p,h1,h2,h3,h4,h5,h6,td,th,div,ul,ol,li,table,img', classes: 'align-left'},
        aligncenter: {selector: 'p,h1,h2,h3,h4,h5,h6,td,th,div,ul,ol,li,table,img', classes: 'align-center'},
        alignright: {selector: 'p,h1,h2,h3,h4,h5,h6,td,th,div,ul,ol,li,table,img', classes: 'align-right'},
        calloutsuccess: {block: 'p', exact: true, attributes: {class: 'callout callout-success'}},
        calloutinfo: {block: 'p', exact: true, attributes: {class: 'callout callout-info'}},
        calloutwarning: {block: 'p', exact: true, attributes: {class: 'callout callout-warning'}},
        calloutdanger: {block: 'p', exact: true, attributes: {class: 'callout callout-danger'}}
      },
    images_upload_url: '/public_photos?record_type=Article&record_id=' + that.id,
    file_picker_types: 'image',
    file_picker_callback: function (cb, value, meta) {
      var input = document.createElement('input');
      input.setAttribute('type', 'file');
      input.setAttribute('accept', 'image/*');

      /*
        Note: In modern browsers input[type="file"] is functional without
        even adding it to the DOM, but that might not be the case in some older
        or quirky browsers like IE, so you might want to add it to the DOM
        just in case, and visually hide it. And do not forget do remove it
        once you do not need it anymore.
      */

      input.onchange = function () {
        var file = this.files[0];

        var reader = new FileReader();
        reader.onload = function () {
          /*
            Note: Now we need to register the blob in TinyMCEs image blob
            registry. In the next release this part hopefully won't be
            necessary, as we are looking to handle it internally.
          */
          var id = 'blobid' + (new Date()).getTime();
          var blobCache =  tinymce.activeEditor.editorUpload.blobCache;
          var base64 = reader.result.split(',')[1];
          var blobInfo = blobCache.create(id, file, base64);
          blobCache.add(blobInfo);

          /* call the callback and populate the Title field with the file name */
          cb(blobInfo.blobUri(), { title: file.name });
        };
        reader.readAsDataURL(file);
      };

      input.click();
    },
    init_instance_callback: function(editor) {
      editor.on('Dirty', function(e) {
        that.isDirty = true
      });
      
      editor.on('focus', function(e) {
        // Safari fix: monitor and prevent scroll changes
        const targetScrollY = window.scrollY;
        let isMonitoring = true;
        
        // Continuously monitor scroll position and force it back
        const scrollMonitor = () => {
          if (!isMonitoring) return;
          
          if (window.scrollY !== targetScrollY) {
            // Immediately force scroll back
            window.scrollTo({
              top: targetScrollY,
              behavior: 'instant'
            });
          }
          
          // Check again on next frame
          requestAnimationFrame(scrollMonitor);
        };
        
        // Start monitoring immediately
        requestAnimationFrame(scrollMonitor);
        
        // Stop monitoring after focus event completes
        setTimeout(() => {
          isMonitoring = false;
        }, 300);
      });
      
      if (!disable_store_callbacks) {
        editor.on('change', function(e) {
          that.$store.dispatch('procedures/updateTaskDescription', [that.index, editor.getContent()])
        });
      }
    },
    plugins: [
      'advlist', 'codesample', 'powerpaste', 'autolink', 'lists', 'link', 'image',
      'searchreplace', 'code', 'directionality', 'fullscreen', 'accordion',
      'media', 'table', 'autosave', 'emoticons', 'autoresize'
    ],
    toolbar: 'undo redo | removeformat | styles | ' +
      'bold italic underline strikethrough superscript subscript fontsizeinput | forecolor backcolor | accordion codesample bullist numlist outdent indent | table link image media blockquote hr | alignleft aligncenter ' +
      'alignright alignjustify |  emoticons | ' +
      ' ltr rtl searchreplace code fullscreen',
    emoticons_database: 'emojis',
    branding: false,
    toolbar_sticky: isInTurboModal ? false : true,
    ui_mode: isInTurboModal ? 'split' : undefined,
    target_list: [
      {title: 'New tab', value: '_blank'},
      {title: 'Same tab', value: ''}
    ],
    link_default_target: '_blank',
    link_assume_external_targets: false
  });
}

export function cleanupTinyMCEUI() {
  document.querySelectorAll('.tox-tinymce-aux, .tox-silver-sink, .tox-dialog-wrap')
    .forEach(el => el.remove());
}
